import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { toast } from 'sonner';
import { ChevronDown, ChevronRight, Circle, CheckCircle2, Clock, Lock, Users } from 'lucide-react';

const DEST = {
  private: { label: 'Kept private', icon: Lock, tone: 'text-white/50' },
  practitioner: { label: 'With your practitioner', icon: Clock, tone: 'text-cyan-200/80' },
  group: { label: 'Shared with the group', icon: Users, tone: 'text-violet-200/80' },
};

const Row = ({ children, testid }) => (
  <div className="rounded-xl border border-white/10 bg-white/[0.03] overflow-hidden"
       data-testid={testid}>
    {children}
  </div>
);

const FeedbackInbox = ({ rows, onRead }) => {
  const [open, setOpen] = useState(null);

  const toggle = async (row) => {
    const next = open === row.id ? null : row.id;
    setOpen(next);
    if (next && row.unread) {
      try {
        await axios.post(`${API}/companion/feedback/${row.id}/read`);
        onRead();
      } catch { /* reading it is not worth an error message */ }
    }
  };

  if (!rows.length) {
    return (
      <p className="text-sm text-white/45" data-testid="feedback-empty">
        Nothing has come back yet. When a practitioner writes on your work, it lands here and
        stays — this list becomes the through-line of your coaching.
      </p>
    );
  }

  return (
    <div className="space-y-2">
      {rows.map((row) => {
        const isOpen = open === row.id;
        const Chevron = isOpen ? ChevronDown : ChevronRight;
        return (
          <Row key={row.id} testid={`feedback-row-${row.id}`}>
            <button
              onClick={() => toggle(row)}
              className="w-full flex items-start justify-between gap-3 px-4 py-3 text-left
                         hover:bg-white/[0.04] transition-colors duration-200"
              data-testid={`feedback-toggle-${row.id}`}
            >
              <span className="flex items-start gap-2 min-w-0">
                <Chevron className="w-4 h-4 text-white/40 shrink-0 mt-0.5" />
                <span className="min-w-0">
                  <span className="block text-sm text-white/90 truncate">
                    {row.from} · {row.section || row.module_title || 'your work'}
                  </span>
                  <span className="block text-xs text-white/40 mt-0.5 truncate">
                    {row.feedback?.slice(0, 90)}
                  </span>
                </span>
              </span>
              <span className="shrink-0 flex items-center gap-2">
                {row.unread && (
                  <span className="rounded-full bg-amber-300/20 border border-amber-300/40
                                   px-2 py-0.5 text-[10px] text-amber-100"
                        data-testid={`feedback-unread-${row.id}`}>
                    new
                  </span>
                )}
                <span className="text-[11px] text-white/35">
                  {row.reviewed_at ? new Date(row.reviewed_at).toLocaleDateString() : ''}
                </span>
              </span>
            </button>
            {isOpen && (
              <div className="px-4 pb-4 space-y-3" data-testid={`feedback-detail-${row.id}`}>
                <p className="text-sm text-white/85 whitespace-pre-line rounded-lg
                              border border-cyan-300/20 bg-cyan-400/[0.06] p-4">
                  {row.feedback}
                </p>
                {row.work_excerpt && (
                  <div>
                    <span className="text-xs uppercase tracking-wider text-white/35">
                      What you handed in
                    </span>
                    <p className="text-xs text-white/55 whitespace-pre-line mt-1">
                      {row.work_excerpt}…
                    </p>
                  </div>
                )}
                {row.module_id && (
                  <Link
                    to={`/dashboard/modules/${row.module_id}`}
                    className="inline-block text-xs text-cyan-300 hover:text-cyan-200"
                    data-testid={`feedback-open-${row.id}`}
                  >
                    Back to {row.section || row.module_title} →
                  </Link>
                )}
              </div>
            )}
          </Row>
        );
      })}
    </div>
  );
};

const WorkRow = ({ row }) => {
  const [open, setOpen] = useState(false);
  const noHandIn = row.hand_in === false;
  const dest = noHandIn
    ? { label: 'Yours only', icon: Lock, tone: 'text-white/50' }
    : (DEST[row.destination] || DEST.private);
  const DestIcon = dest.icon;
  const done = row.status === 'submitted'
    || (noHandIn && row.answered > 0 && row.answered >= row.fields);

  return (
    <Row testid={`work-row-${row.worksheet_id}`}>
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-start justify-between gap-3 px-4 py-3 text-left
                   hover:bg-white/[0.04] transition-colors duration-200"
        data-testid={`work-toggle-${row.worksheet_id}`}
      >
        <span className="flex items-start gap-2 min-w-0">
          {done ? <CheckCircle2 className="w-4 h-4 text-teal-300 shrink-0 mt-0.5" />
                : <Circle className="w-4 h-4 text-white/30 shrink-0 mt-0.5" />}
          <span className="min-w-0">
            <span className="block text-sm text-white/90 truncate">{row.title}</span>
            <span className="block text-xs text-white/40 mt-0.5 truncate">
              {row.section ? `${row.section} · ` : ''}Module {row.module_order}
            </span>
          </span>
        </span>
        <span className={`shrink-0 flex items-center gap-1.5 text-[11px] ${dest.tone}`}>
          <DestIcon className="w-3 h-3" />
          {done ? dest.label : `${row.answered}/${row.fields} answered`}
        </span>
      </button>
      {open && (
        <div className="px-4 pb-4 space-y-3" data-testid={`work-detail-${row.worksheet_id}`}>
          {row.write_up ? (
            <p className="text-sm text-white/75 whitespace-pre-line rounded-lg border
                          border-white/10 bg-black/20 p-4 max-h-72 overflow-y-auto">
              {row.write_up}
            </p>
          ) : (
            <p className="text-sm text-white/50">
              {noHandIn
                ? 'Nothing to hand in here — your answers save themselves as you go.'
                : 'Started but not handed in yet — your answers are saved as you type.'}
            </p>
          )}
          <div className="flex flex-wrap items-center gap-4">
            <Link
              to={`/dashboard/modules/${row.module_id}`}
              className="text-xs text-cyan-300 hover:text-cyan-200"
              data-testid={`work-open-${row.worksheet_id}`}
            >
              {done ? 'Open the exercise' : 'Carry on with it'} →
            </Link>
            {row.submitted_at && (
              <span className="text-[11px] text-white/35">
                Handed in {new Date(row.submitted_at).toLocaleDateString()}
              </span>
            )}
            {row.has_feedback && (
              <span className="text-[11px] text-teal-200/70">Feedback received</span>
            )}
          </div>
        </div>
      )}
    </Row>
  );
};

const Group = ({ title, note, children, testid }) => (
  <section className="space-y-3" data-testid={testid}>
    <div>
      <h2 className="text-base md:text-lg font-semibold text-white">{title}</h2>
      {note && <p className="text-sm text-white/45 mt-0.5">{note}</p>}
    </div>
    {children}
  </section>
);

/** Everything they have written on the program, and everything written back to them. */
export const MyWork = () => {
  const [work, setWork] = useState(null);
  const [feedback, setFeedback] = useState(null);

  const load = useCallback(() => {
    axios.get(`${API}/companion/work`).then((r) => setWork(r.data))
      .catch(() => toast.error('Your work could not be loaded'));
    axios.get(`${API}/companion/feedback`).then((r) => setFeedback(r.data))
      .catch(() => setFeedback({ feedback: [], unread: 0 }));
  }, []);

  useEffect(() => { load(); }, [load]);

  if (!work) {
    return <p className="text-sm text-white/50" data-testid="work-loading">Gathering your work…</p>;
  }

  const isComplete = (r) => r.status === 'submitted'
    || (r.hand_in === false && r.answered > 0 && r.answered >= r.fields);
  const submitted = work.submitted.filter(isComplete);
  const started = work.submitted.filter((r) => !isComplete(r));

  return (
    <div className="space-y-10" data-testid="my-work">
      <Group
        title="What your practitioners have said"
        note="Every piece of feedback, kept in one place."
        testid="work-feedback"
      >
        <FeedbackInbox rows={feedback?.feedback || []} onRead={load} />
      </Group>

      {started.length > 0 && (
        <Group title="In progress" note="Saved as you go — pick it back up any time." testid="work-started">
          <div className="space-y-2">
            {started.map((row) => <WorkRow key={row.worksheet_id} row={row} />)}
          </div>
        </Group>
      )}

      <Group title="Completed" note="Your own words, kept." testid="work-submitted">
        {submitted.length ? (
          <div className="space-y-2">
            {submitted.map((row) => <WorkRow key={row.worksheet_id} row={row} />)}
          </div>
        ) : (
          <p className="text-sm text-white/45" data-testid="work-submitted-empty">
            Nothing finished yet. Anything you complete on the program lands here.
          </p>
        )}
      </Group>

      <Group title="Waiting on you" note="Worksheets in the modules you can reach." testid="work-todo">
        {work.to_do.length ? (
          <div className="space-y-2">
            {work.to_do.map((row) => (
              <Row key={row.worksheet_id} testid={`todo-row-${row.worksheet_id}`}>
                <Link
                  to={`/dashboard/modules/${row.module_id}`}
                  className="flex items-center justify-between gap-3 px-4 py-3
                             hover:bg-white/[0.04] transition-colors duration-200"
                  data-testid={`todo-open-${row.worksheet_id}`}
                >
                  <span className="min-w-0">
                    <span className="block text-sm text-white/90 truncate">{row.title}</span>
                    <span className="block text-xs text-white/40 mt-0.5 truncate">
                      {row.section ? `${row.section} · ` : ''}Module {row.module_order}
                    </span>
                  </span>
                  <span className="text-[11px] text-white/40 shrink-0">
                    {row.fields} question{row.fields === 1 ? '' : 's'} →
                  </span>
                </Link>
              </Row>
            ))}
          </div>
        ) : (
          <p className="text-sm text-white/45" data-testid="work-todo-empty">
            You are up to date on the worksheets available to you.
          </p>
        )}
      </Group>
    </div>
  );
};

export default MyWork;
